import {useCallback, useEffect, useState} from 'react';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';

export type RecentAccessEntry = {
  typeId: string;
  recordId: string;
  accessedAt: number;
};

const STORAGE_PREFIX = 'matic_relay_recent_objects_v1';
// Stored well past whatever display cap a consumer applies, so a few
// recently-deleted/renamed records don't immediately starve the visible
// list down to nothing.
const MAX_STORED = 20;

function storageKey(tenantId: string): string {
  return `${STORAGE_PREFIX}:${tenantId}`;
}

function readStored(tenantId: string): RecentAccessEntry[] {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentAccessEntry =>
        Boolean(e) &&
        typeof e.typeId === 'string' &&
        typeof e.recordId === 'string' &&
        typeof e.accessedAt === 'number'
    );
  } catch {
    return [];
  }
}

/**
 * Tracks the most recently opened MDM object records (Projects, Suppliers,
 * etc.) in the Library, per tenant, in localStorage — no backend, nothing to
 * name or manage. Replaces the old scope-saving "Pinned" section in the LHS
 * picker: opening a record's page bumps it to the front automatically.
 */
export function useRecentlyAccessedObjects() {
  const tenantId = useRequiredTenantId();
  const [entries, setEntries] = useState<RecentAccessEntry[]>(() =>
    readStored(tenantId)
  );

  useEffect(() => {
    setEntries(readStored(tenantId));
  }, [tenantId]);

  const recordAccess = useCallback(
    (typeId: string, recordId: string) => {
      setEntries((prev) => {
        const next = [
          {typeId, recordId, accessedAt: Date.now()},
          ...prev.filter(
            (e) => !(e.typeId === typeId && e.recordId === recordId)
          ),
        ].slice(0, MAX_STORED);
        try {
          localStorage.setItem(storageKey(tenantId), JSON.stringify(next));
        } catch {
          // Storage full/unavailable (private browsing) — the recency list
          // is a nice-to-have, never worth surfacing an error for.
        }

        return next;
      });
    },
    [tenantId]
  );

  return {recentlyAccessed: entries, recordAccess};
}
